<script lang="ts" setup>
import { ref, Ref, computed, onBeforeMount, onBeforeUnmount, inject } from 'vue';
import { useEvents } from '@/plugins/events';
import properties from '@/components/forms/properties';
import { RootEntry } from '@/components/builder/form/entries/RootEntry';
import { instanceOfRichTextEntry, RichTextType, RichTextEntry } from '.';
import { FormBuilderContract } from '@/components/builder/form';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { useControlReload } from '@/components/forms/helpers/RelatedControlsHelper';
import { useDynamicTrigger, useDynamicTriggerChannelId } from '@/components/forms/helpers/DynamicTriggerHelper';
import { TriggerMoments, TriggerModes } from '@/components/builder/form/traits/HasTriggers';
import { FieldTrigger } from '@/components/forms/properties';

defineOptions({
    name: 'rich-text-blueprint',
    components: {
        ...properties
    }
});

const props = defineProps({
  "blueprint": null,
  "entry": null,
  "form": null,
  "parent": null,
  "index": null
});

const getDynamicTriggerChannelId = useDynamicTriggerChannelId(() => props.form.blueprint.id);

const { $events } = useEvents();
const blueprint = computed(() => props.blueprint);
const entryData = ref(new RichTextEntry()) as Ref<RichTextEntry>;
const level = inject<number>('level', 0);

const loadControlData = () =>
{
    return props.form.document.initEntry(props.blueprint, entryData.value, instanceOfRichTextEntry, props.index);
};

const setControlData = () =>
{
    entryData.value = loadControlData();
};

entryData.value = loadControlData();

onBeforeMount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOn(setControlData);
        else
            useControlReload($events, props.blueprint.name, level).eventOn(setControlData);
    }
});

onBeforeUnmount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOff();
        else
            useControlReload($events, props.blueprint.name, level).eventOff();
    }
});

const value = computed({
    get()
    {
        const { data: value } = entryData.value;

        return value === null || (readonly.value && props.blueprint.defaultValue)
            ? props.form.expressions.executeExpression(props.blueprint.defaultValue, props.index)
            : value;
    },
    set(value: string)
    {
        entryData.value.data = value;

        useDynamicTrigger($events, getDynamicTriggerChannelId()).emit(TriggerMoments.onChange, props.blueprint, props.parent?.type === 'table' ? props.index : null);
    }
});

const readonly = computed(() => props.form.expressions.readonly(props.blueprint, false, props.index));
const required = computed(() => props.form.expressions.required(props.blueprint, props.index));
</script>

<template>
    <form-component-wrapper class="text-component" :form="form" :parent="parent" :blueprint="blueprint">
        <template #default>
            <div class="form-group">
                <form-label :form="form" :blueprint="blueprint" :required="required" :entry="entryData" />
                <editor v-model="value" :id="`editor-${blueprint.id}${form.designMode() ? '-design': ''}`" :disabled="readonly" />
                <form-error-message :entry="entryData" name="value" />
                <form-error-message :entry="entryData" name="custom" />
                <form-help :form="form" :blueprint="blueprint" />
            </div>
        </template>
        <template #properties>
            <field-name :form="form" :blueprint="blueprint" v-model="blueprint.name" />
            <IdeoTabs pills nav-class="nav-justified" content-class="mt-3 docs-container">
                <IdeoTab :title="$t('[[[Konfiguracja]]]')" active>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.label[locale]" :label="$t('[[[Etykieta]]]')" />
                    </ideo-form-localize>
                    <field-checkbox v-model="blueprint.showLabel" :label="$t('[[[Wyświetlaj etykietę]]]')" />
                    <field-condition v-model="blueprint.defaultValue" :form="form" :label="$t('[[[Wartość domyślna]]]')" type="text" />
                    <field-minmax v-model="blueprint.characters" :label="$t('[[[Ilość znaków]]]')" :naturals="true" />
                    <ideo-form-localize v-slot="{ locale }">
                        <field-textarea v-model="blueprint.help[locale]" :label="$t('[[[Pomoc]]]')" />
                    </ideo-form-localize>
                    <field-visible :form="form" :blueprint="blueprint" />
                    <field-readonly :form="form" :blueprint="blueprint" />
                    <field-required :form="form" :blueprint="blueprint" />
                    <field-error :form="form" :blueprint="blueprint" />
                </IdeoTab>
                <IdeoTab :title="$t('[[[Wyzwalacze]]]')">
                    <FieldTrigger v-model="blueprint.serverSideTriggers" :mode="TriggerModes.ServerSide" :blueprint="blueprint" />
                    <FieldTrigger v-model="blueprint.clientSideTriggers" :mode="TriggerModes.ClientSide" :blueprint="blueprint" />
                </IdeoTab>
            </IdeoTabs>
        </template>
    </form-component-wrapper>
</template>
